import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getConfig } from '../../config/runtimeConfig';
import { useSession } from '../layout/TopBar';
import { HomePage } from './HomePage';

/**
 * `/`: the Home feed for a signed-in person, Explore for everyone else.
 *
 * Home is "what's new in your workspace" and has nothing to show a guest — it
 * rendered an empty page — while Explore already serves guests from /public.
 * A replace, not a push, so Back does not return to a URL that only bounces.
 *
 * Where a website owns `/` (RuntimeConfig.website) a page load of `/` never
 * reaches the app, except the dashboard's `?return=` bounce, which main.tsx
 * settles before the router runs. So this runs on navigation inside the app
 * (the logo, sign-out): a signed-in person goes to Collections, as the CDN
 * sends them on a fresh load, and a guest leaves for the website.
 */
export function RootRoute() {
  const { data: me, isLoading, isError } = useSession();
  const { search } = useLocation();
  if (isLoading) return <div style={{ flex: 1 }} />;
  // A guest's session check answers 401, which currentSession turns into null.
  // An ERROR is something else — a 5xx or a dropped connection — and says
  // nothing about who this is, so it must not bounce a signed-in person away
  // from Home.
  if (isError) return <HomePage />;
  const website = getConfig().website;
  if (!me?.userId) return website ? <ToWebsite /> : <Navigate to={{ pathname: '/explore', search }} replace />;
  return website ? <Navigate to={{ pathname: '/collections', search }} replace /> : <HomePage />;
}

/** A page load of `/`, which the CDN serves from the website. Without the
 *  query: a `?return=` is what sends `/` to the app, so keeping it would loop. */
function ToWebsite() {
  useEffect(() => window.location.replace('/'), []);
  return <div style={{ flex: 1 }} />;
}
